import { ContentType } from "@brookbush/database";
import type { Video } from "@brookbush/types";

import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";
import {
	ChevronDownIcon,
	ChevronUpIcon,
	HeartIcon,
	MessageCircleIcon,
} from "lucide-react";
import { useState } from "react";

import BookmarkButton from "~/components/BookmarkButton";
import ContentCard from "~/components/ContentCard";
import LikeButton from "~/components/LikeButton";
import ShareButton from "~/components/ShareButton";

const VideoDetails = ({ video }: { video: Video }) => {
	const [isExpanded, setIsExpanded] = useState(false);

	return (
		<section className="laptop:mt-0 mt-[59%] mb-2 flex flex-col gap-4">
			<div className="flex laptop:flex-row flex-col laptop:items-start laptop:justify-between gap-4 laptop:gap-4">
				<h1 className="font-bold text-xl xl:text-2xl">{video.title}</h1>

				<div className="flex shrink-0 items-center gap-2 self-start sm:gap-3">
					<ShareButton />
					<BookmarkButton
						contentId={video._id}
						contentType={ContentType.VIDEO}
					/>
					<LikeButton contentId={video._id} contentType={ContentType.VIDEO} />
				</div>
			</div>

			<div className="flex items-center gap-4 laptop:gap-4">
				<div className="flex items-center gap-1.5">
					<HeartIcon size={16} />
					<span>
						{video.likeCount} Like{video.likeCount === 1 ? "" : "s"}
					</span>
				</div>

				<div className="flex items-center gap-1.5">
					<MessageCircleIcon size={16} />
					<span>
						{video.commentCount} Comment{video.commentCount === 1 ? "" : "s"}
					</span>
				</div>
			</div>

			<p
				className={clsx("line-clamp-2 laptop:line-clamp-none", {
					"line-clamp-none": isExpanded,
				})}
			>
				{video.description}
			</p>

			<AnimatePresence mode="wait">
				{isExpanded && (
					<motion.div
						initial={{
							opacity: 0,
							y: -20,
						}}
						animate={{
							opacity: 1,
							y: 0,
							transition: {
								duration: 0.2,
							},
						}}
						exit={{
							opacity: 0,
							y: -20,
							transition: {
								duration: 0.1,
							},
						}}
						className="flex flex-col gap-4"
					>
						<div className="flex flex-col gap-4">
							<h2 className="font-semibold text-lg text-slate-900 dark:text-white">
								Also Known As:
							</h2>

							<ul className="ml-2 flex list-inside list-disc flex-wrap gap-1.5 sm:gap-4">
								{video.alsoKnownAs?.map((alsoKnownAs) => (
									<li key={alsoKnownAs}>{alsoKnownAs}</li>
								))}
							</ul>
						</div>

						<div className="flex flex-col gap-4">
							<h2 className="font-semibold text-lg text-slate-900 dark:text-white">
								Appears In:
							</h2>

							<div className="grid grid-cols-1 tablet:grid-cols-3 gap-4">
								{video.courses?.map((course) => (
									<ContentCard key={course._id} {...course} _type="COURSE" />
								))}
							</div>
						</div>
					</motion.div>
				)}
			</AnimatePresence>

			<button
				onClick={() => setIsExpanded(!isExpanded)}
				className="flex items-center justify-center gap-1 rounded-full py-1.5 pr-2.5 pl-3 font-medium hover:bg-slate-50 dark:hover:bg-slate-700"
			>
				{isExpanded ? (
					<>
						View Less <ChevronUpIcon size={20} strokeWidth={2.4} />
					</>
				) : (
					<>
						View More <ChevronDownIcon size={20} strokeWidth={2.4} />
					</>
				)}
			</button>
		</section>
	);
};

export default VideoDetails;
